// SQ-7544: shared-device kiosk error recovery.
//
// When the kiosk webapp hits an uncaught error it can be left on a wedged white
// screen that only a device reboot clears. The global error handler (see
// `app/instance-initializers/kiosk-error-recovery.ts`) uses this pure decision
// to auto-recover to the welcome screen, with a loop guard so a persistent
// error doesn't turn into an endless reload-thrash.

// How long a burst of recoveries is counted together before the count resets.
export const RECOVERY_WINDOW_MS = 120_000; // 2 minutes
// Max auto-recoveries allowed within a single window before we give up.
export const MAX_RECOVERIES = 2;

// How long the try-again screen is shown on a kiosk before it auto-recovers
// back to its entry URL (long enough to read, short enough to not need staff).
export const KIOSK_TRY_AGAIN_RECOVERY_DELAY_MS = 10_000;

// sessionStorage keys shared between the session service (which persists the
// entry URL) and the global error handler (which reads the recovery state).
export const KIOSK_ENTRY_URL_KEY = 'kioskEntryUrl';
export const KIOSK_LAST_RECOVERY_AT_KEY = 'kioskLastRecoveryAt';
export const KIOSK_RECOVERY_COUNT_KEY = 'kioskRecoveryCount';

export type KioskRecoveryState = {
  // The kiosk entry URL persisted for shared devices, or null when this is not
  // a shared device (in which case we never auto-recover).
  entryUrl: string | null;
  now: number;
  lastRecoveryAt: number | null;
  recentCount: number;
};

export type KioskRecoveryDecision = {
  recover: boolean;
  reason: 'not-shared-device' | 'cooldown-loop-guard' | 'recovering';
  // The recovery count to persist when recovering (ignored when recover=false).
  nextCount: number;
};

export function decideKioskRecovery({
  entryUrl,
  now,
  lastRecoveryAt,
  recentCount,
}: KioskRecoveryState): KioskRecoveryDecision {
  if (!entryUrl) {
    return { recover: false, reason: 'not-shared-device', nextCount: 0 };
  }

  const withinWindow = lastRecoveryAt != null && now - lastRecoveryAt < RECOVERY_WINDOW_MS;
  const effectiveCount = withinWindow ? recentCount : 0;

  if (effectiveCount >= MAX_RECOVERIES) {
    return { recover: false, reason: 'cooldown-loop-guard', nextCount: effectiveCount };
  }

  return { recover: true, reason: 'recovering', nextCount: effectiveCount + 1 };
}
